import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Grid from '@mui/material/Grid2';
import {
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Paper,
  Stack,
  IconButton,
  Divider,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { OptionData } from '@/__generated__/graphql';

type Mode = 'Edit' | 'ReadOnly';

type EditRow = {
  value: string;
  min: string;
  max: string;
  minIncluded: boolean;
  maxIncluded: boolean;
  weight?: number | null;
  touched?: { min?: boolean; max?: boolean };
};

type OptionsInput = OptionData[];
type OptionsOutput = OptionData[];

const emptyRow = (): EditRow => ({
  value: '',
  min: '',
  max: '',
  minIncluded: false,
  maxIncluded: false,
  weight: null,
  touched: {},
});

const numberPattern = /^-?\d+(\.\d+)?$/;
const getRowErrors = (r: EditRow) => {
  const minValid = numberPattern.test(r.min.trim());
  const maxValid = numberPattern.test(r.max.trim());
  const logical =
    minValid && maxValid && parseFloat(r.min) > parseFloat(r.max)
      ? 'Minimum cannot be greater than Maximum'
      : '';
  return { minValid, maxValid, logical };
};

export const MultiRangeOptions = ({
  fieldKey,
  mode = 'Edit',
  optionsData,
  onChange,
  onValidityChange,
}: {
  fieldKey: string | number;
  mode?: Mode;
  optionsData: OptionsInput;
  onChange: (next: OptionsOutput) => void;
  onValidityChange?: (isValid: boolean) => void;
}) => {
  const isReadOnly = mode === 'ReadOnly';
  const [rangesEnabled, setRangesEnabled] = useState<boolean>(false);
  const [rows, setRows] = useState<EditRow[]>([emptyRow()]);

  const lastEmittedRef = useRef<string>('');

  useEffect(() => {
    const items = (optionsData ?? []).filter(Boolean);
    setRangesEnabled(items.length > 0);
    setRows(
      items.length
        ? items.map((r) => ({
            value: r.value ?? '',
            min: r.min !== null ? String(r.min) : '',
            max: r.max !== null ? String(r.max) : '',
            minIncluded: !!r.minIncluded,
            maxIncluded: !!r.maxIncluded,
            weight: r.weight ?? null,
            touched: {},
          }))
        : [emptyRow()]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldKey]);

  const allValid = useMemo(() => {
    if (!rangesEnabled) return true;
    return rows.every((r) => {
      const { minValid, maxValid, logical } = getRowErrors(r);
      return minValid && maxValid && !logical;
    });
  }, [rows, rangesEnabled]);

  useEffect(() => {
    onValidityChange?.(allValid);
  }, [allValid, onValidityChange]);

  useEffect(() => {
    if (!rangesEnabled) {
      if (lastEmittedRef.current !== '[]') {
        onChange([]);
        lastEmittedRef.current = '[]';
      }
      return;
    }
    if (allValid) {
      const out: OptionsOutput = rows.map((r) => ({
        __typename: 'OptionData',
        value: r.value,
        min: parseFloat(r.min),
        max: parseFloat(r.max),
        minIncluded: r.minIncluded,
        maxIncluded: r.maxIncluded,
        weight: r.weight ?? null,
      }));
      const key = JSON.stringify(out);
      if (key !== lastEmittedRef.current) {
        onChange(out);
        lastEmittedRef.current = key;
      }
    }
  }, [rangesEnabled, rows, allValid, onChange]);

  const updateRow = useCallback(
    (index: number, patch: Partial<EditRow>) =>
      setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r))),
    [setRows]
  );

  const blurRow = useCallback(
    (index: number, key: 'min' | 'max') =>
      setRows((prev) =>
        prev.map((r, i) => (i === index ? { ...r, touched: { ...r.touched, [key]: true } } : r))
      ),
    [setRows]
  );

  const addRow = useCallback(() => setRows((p) => [...p, emptyRow()]), [setRows]);

  const removeRow = useCallback(
    (index: number) => setRows((p) => (p.length === 1 ? p : p.filter((_, i) => i !== index))),
    [setRows]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Ranges
        </Typography>
        <FormControlLabel
          sx={{ m: 0 }}
          label="Enable Ranges"
          control={
            <Checkbox
              checked={rangesEnabled}
              onChange={(e) => setRangesEnabled(e.target.checked)}
              disabled={isReadOnly}
            />
          }
        />
      </Stack>

      {rangesEnabled && (
        <Box>
          <Divider sx={{ mb: 1 }} />

          {rows.map((row, index) => {
            const { minValid, maxValid, logical } = getRowErrors(row);
            return (
              <Paper
                key={fieldKey}
                variant="outlined"
                sx={{ p: 1.5, mb: 1.25, borderColor: 'divider' }}
              >
                <Grid container spacing={1.25}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="e.g., 0-2mm (<2mm)"
                      label="Value"
                      value={row.value}
                      onChange={(e) => updateRow(index, { value: e.target.value })}
                      disabled={isReadOnly}
                    />
                  </Grid>

                  <Grid
                    size={{ xs: 12, md: 6 }}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <TextField
                      size="small"
                      fullWidth
                      label="Min"
                      placeholder="Min"
                      value={row.min}
                      onChange={(e) => updateRow(index, { min: e.target.value })}
                      onBlur={() => blurRow(index, 'min')}
                      error={row.touched?.min && !minValid}
                      helperText={row.touched?.min && !minValid ? 'Enter a valid number' : ' '}
                      slotProps={{ input: { inputMode: 'decimal' } }}
                      disabled={isReadOnly}
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, md: 6 }}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                  >
                    <FormControlLabel
                      sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                      label="Min Included"
                      control={
                        <Checkbox
                          size="small"
                          checked={row.minIncluded}
                          onChange={(e) => updateRow(index, { minIncluded: e.target.checked })}
                          disabled={isReadOnly}
                        />
                      }
                    />
                  </Grid>

                  <Grid
                    size={{ xs: 12, md: 6 }}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <TextField
                      size="small"
                      fullWidth
                      label="Max"
                      placeholder="Max"
                      value={row.max}
                      onChange={(e) => updateRow(index, { max: e.target.value })}
                      onBlur={() => blurRow(index, 'max')}
                      error={row.touched?.max && !maxValid}
                      helperText={row.touched?.max && !maxValid ? 'Enter a valid number' : ' '}
                      slotProps={{ input: { inputMode: 'decimal' } }}
                      disabled={isReadOnly}
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, md: 6 }}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                  >
                    <FormControlLabel
                      sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                      label="Max Included"
                      control={
                        <Checkbox
                          size="small"
                          checked={row.maxIncluded}
                          onChange={(e) => updateRow(index, { maxIncluded: e.target.checked })}
                          disabled={isReadOnly}
                        />
                      }
                    />
                  </Grid>

                  <Grid
                    size={{ xs: 12 }}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Typography variant="caption" color="error">
                      {logical || ' '}
                    </Typography>
                    <IconButton
                      aria-label="remove range"
                      onClick={() => removeRow(index)}
                      disabled={isReadOnly || rows.length === 1}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            );
          })}

          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={addRow}
            disabled={isReadOnly}
            size="small"
          >
            Add Range
          </Button>
        </Box>
      )}
    </Paper>
  );
};
